import '@fontsource/abril-fatface';
import '@fontsource/gruppo';
import '@fontsource-variable/montserrat';

export const colors = {
  brand: '#0F9B25',
  lightBrand: '#26B33C',
  'c-102': '#A4AFCE',
  'c-103': '#68728E',
  'c-104': '#4F5975',
  'c-105': '#374059',
  'c-106': '#1A2031',
  'c-107': '#0E121D',
  'c-108': '#0B0E15',
  'c-109': '#030408',
  'c-100': '#FF3838',
  'c-300': '#34DD4F',
  bg: '#121219',
  gray6f: '#6F6F6F',
  gray38: '#383838',
  gray1304: 'rgba(13, 13, 17, 0.4)',
  shadowGray: '#272730',
  errorRed: '#FF494A',
  darkGreen: '#164D2C',
  grassGreen: '#72B055',
  slate: '#242C44',
  hotRed: '#FF5151',
  brickRed: '#FF5833',
};

export const borders = {
  normal: `1px solid ${colors['c-106']}`,
  dashed105: `1px dashed ${colors['c-105']}`,
  dashed103: `1px dashed ${colors['c-103']}`,
  solid104: `1px solid ${colors['c-104']}`,
  solid105: `1px solid ${colors['c-105']}`,
  white20: `1px solid rgba(255, 255, 255, 0.2)`,
  lightGreen: `1px solid ${colors['c-300']}`,
  red100: `1px solid ${colors['c-100']}`,
};

const solidButtonBasicStyle = {
  color: 'white',
  fontSize: 'md',
  fontWeight: 600,
  borderRadius: '6px',
  h: '3rem',
  _disabled: {
    opacity: 1,
    bg: 'gray38',
    cursor: 'not-allowed',
    color: 'gray6f',
    img: {
      opacity: 0.4,
    },
  },
};

const bigColoredOutlineButtonBasicStyle = {
  w: '10rem',
  h: '5rem',
  bg: 'transparent',
  fontSize: 'xl',
  fontWeight: 500,
  borderRadius: '6px',
  _disabled: {
    cursor: 'not-allowed',
    opacity: 0.2,
  },
  _active: {
    bg: 'transparent',
  },
};

const overrides = {
  colors,
  styles: {
    global: {
      html: {
        fontSize: 'min(1vw, 1.6vh)',
      },
      // styles for the `body`
      body: {
        bg: '#121219',
        color: 'white',
        fontFamily: 'Montserrat Variable',
      },
      '*': {
        scrollbarColor: `${colors['c-105']} ${colors['c-106']}`,
        scrollbarWidth: 'thin',
      },
      '::-webkit-scrollbar': {
        w: '0.25rem',
        bgColor: colors['c-106'],
        borderRadius: '10px',
      },
      '::-webkit-scrollbar-thumb': {
        bgColor: colors['c-105'],
        borderRadius: '10px',
      },
    },
  },
  textStyles: {
    card: {
      fontFamily: 'Abril Fatface',
      fontSize: '3xl',
      lineHeight: '120%',
      fontWeight: 400,
    },
    // for the home page
    h1: {
      fontFamily: 'Gruppo',
      fontSize: '2.5rem',
      color: 'white',
      lineHeight: '100%',
    },
    p1: {
      fontFamily: 'Gruppo',
      fontSize: 'xl',
      color: 'whiteAlpha.600',
    },
    p3: {
      fontSize: 'sm',
      color: 'whiteAlpha.400',
      lineHeight: '121%',
      fontWeight: 'medium',
    },
  },
  components: {
    Button: {
      baseStyle: {
        _focus: {
          boxShadow: 'none',
        },
      },
      variants: {
        normal: {
          bg: 'brand',
          ...solidButtonBasicStyle,
          _hover: {
            bg: 'lightBrand',
            _disabled: {
              bg: 'gray38',
            },
          },
        },
        darkSolid: {
          bg: 'c-105',
          ...solidButtonBasicStyle,
          _hover: {
            bg: 'c-106',
            _disabled: {
              bg: 'gray38',
            },
          },
        },
        redSolid: {
          bg: 'c-100',
          ...solidButtonBasicStyle,
          _hover: {
            bg: 'brickRed',
            _disabled: {
              bg: 'gray38',
            },
          },
        },
        whiteBrand: {
          ...solidButtonBasicStyle,
          bg: '#fff',
          color: '#000',
        },
        outline: {
          bg: 'transparent',
          color: 'c-103',
          fontSize: 'md',
          fontWeight: 500,
          borderRadius: '6px',
          h: '3.25rem',
          borderColor: 'c-106',
          _hover: {
            _disabled: {
              bg: 'gray38',
            },
            borderColor: 'brand',
            color: 'brand',
            bg: 'transparent',
          },
          _disabled: {
            opacity: 1,
            bg: 'gray38',
            cursor: 'not-allowed',
            img: {
              opacity: 0.4,
            },
          },
          _active: {
            bg: 'transparent',
          },
        },
        bigGreenOutline: {
          border: borders.lightGreen,
          color: 'c-300',
          ...bigColoredOutlineButtonBasicStyle,
          _hover: {
            _disabled: {
              opacity: 0.2,
              color: 'c-300',
            },
            bg: 'c-300',
            color: 'white',
          },
        },
        bigGreenBrand: {
          ...bigColoredOutlineButtonBasicStyle,
          _disabled: {
            opacity: 0.2,
            color: 'c-300',
          },
          bg: 'c-300',
          color: 'white',
        },
        bigRedOutline: {
          border: borders.red100,
          color: 'c-100',
          ...bigColoredOutlineButtonBasicStyle,
          _hover: {
            _disabled: {
              opacity: 0.2,
              color: 'c-100',
            },
            bg: 'c-100',
            color: 'white',
          },
        },
        bigRedBrand: {
          ...bigColoredOutlineButtonBasicStyle,
          _disabled: {
            opacity: 0.2,
            color: 'c-100',
          },
          bg: 'c-100',
          color: 'white',
        },
      },
    },
  },
  zIndices: {
    modal: 80, // 设置新的 z-index 值, fix mobile时登录弹窗的层级问题
  },
};

export default overrides;
